import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Clock, DollarSign, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { chargersApi, tariffsApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { cn, formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  PageHeader,
  PageShell,
  ScrollHint,
  TextField,
  apiMessage,
  fieldControl,
  glassPanel,
  glassStrong,
  glassSubtle,
  pillButton,
} from '@/pages/glass';

const emptyForm = {
  name: '',
  currency: 'AED',
  fixed_session_fee: 0,
  price_per_kwh: 0,
  parking_fee_per_minute: 0,
  parking_grace_period_minutes: 0,
  is_active: true,
  time_of_use_rates: [],
};

const moneyError = (value, label) => {
  if (value === '' || value === null || value === undefined) return `Enter a ${label}.`;
  const number = Number(value);
  if (!Number.isFinite(number)) return `${label} must be a number.`;
  if (number < 0) return `${label} cannot be negative.`;
  return '';
};

const rangesOverlap = (left, right) => left.start < right.end && right.start < left.end;

const validateForm = (form) => {
  const errors = {};
  const name = form.name.trim();
  if (!name) errors.name = 'Enter a tariff name.';
  else if (name.length > 80) errors.name = 'Keep the name under 80 characters.';

  const fixed = moneyError(form.fixed_session_fee, 'session fee');
  const energy = moneyError(form.price_per_kwh, 'price per kWh');
  const parking = moneyError(form.parking_fee_per_minute, 'parking fee');
  if (fixed) errors.fixed_session_fee = fixed;
  if (energy) errors.price_per_kwh = energy;
  if (parking) errors.parking_fee_per_minute = parking;

  if (form.parking_grace_period_minutes === '' || form.parking_grace_period_minutes === null) {
    errors.parking_grace_period_minutes = 'Enter a grace period.';
  } else {
    const grace = Number(form.parking_grace_period_minutes);
    if (!Number.isInteger(grace) || grace < 0) errors.parking_grace_period_minutes = 'Grace period must be a whole number of minutes, zero or greater.';
  }

  const rateErrors = [];
  const parsed = form.time_of_use_rates.map((rate) => ({
    start: Number(rate.start_hour),
    end: Number(rate.end_hour),
    price: Number(rate.price_per_kwh),
    rawStart: rate.start_hour,
    rawEnd: rate.end_hour,
    rawPrice: rate.price_per_kwh,
  }));
  parsed.forEach((rate, index) => {
    const label = `Rate ${index + 1}`;
    if (rate.rawStart === '' || !Number.isInteger(rate.start) || rate.start < 0 || rate.start > 23) {
      rateErrors.push(`${label}: start hour must be a whole number from 0 to 23.`);
    }
    if (rate.rawEnd === '' || !Number.isInteger(rate.end) || rate.end < 0 || rate.end > 24) {
      rateErrors.push(`${label}: end hour must be a whole number from 0 to 24.`);
    } else if (Number.isInteger(rate.start) && rate.end <= rate.start) {
      rateErrors.push(`${label}: end hour must be later than the start hour.`);
    }
    if (rate.rawPrice === '' || !Number.isFinite(rate.price) || rate.price < 0) {
      rateErrors.push(`${label}: price per kWh must be zero or greater.`);
    }
  });
  parsed.forEach((rate, index) => {
    if (!Number.isInteger(rate.start) || !Number.isInteger(rate.end) || rate.end <= rate.start) return;
    parsed.slice(index + 1).forEach((other, offset) => {
      if (!Number.isInteger(other.start) || !Number.isInteger(other.end) || other.end <= other.start) return;
      if (rangesOverlap(rate, other)) rateErrors.push(`Rates ${index + 1} and ${index + offset + 2} overlap.`);
    });
  });
  if (rateErrors.length) errors.time_of_use_rates = rateErrors.join(' ');
  return errors;
};

const toPayload = (form) => ({
  name: form.name.trim(),
  currency: 'AED',
  fixed_session_fee: Number(form.fixed_session_fee),
  price_per_kwh: Number(form.price_per_kwh),
  parking_fee_per_minute: Number(form.parking_fee_per_minute),
  parking_grace_period_minutes: Number(form.parking_grace_period_minutes),
  is_active: Boolean(form.is_active),
  time_of_use_rates: form.time_of_use_rates.map((rate) => ({
    start_hour: Number(rate.start_hour),
    end_hour: Number(rate.end_hour),
    price_per_kwh: Number(rate.price_per_kwh),
  })),
});

const TariffsPage = () => {
  const { can } = useAuth();
  const canManage = can('tariffs.manage');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [chargerWarning, setChargerWarning] = useState('');
  const [tariffs, setTariffs] = useState([]);
  const [chargers, setChargers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [attempted, setAttempted] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const loaded = useRef(false);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setChargerWarning('');
    try {
      const [tariffsResult, chargersResult] = await Promise.allSettled([
        tariffsApi.getAll(),
        chargersApi.getAll(),
      ]);
      if (tariffsResult.status === 'rejected') {
        const message = apiMessage(tariffsResult.reason, 'Failed to load tariffs');
        if (!loaded.current) setLoadError(message);
        else toast.error(message);
        return;
      }
      setTariffs(tariffsResult.value.data || []);
      setLoadError('');
      loaded.current = true;
      if (chargersResult.status === 'fulfilled') setChargers(chargersResult.value.data || []);
      else {
        setChargers([]);
        setChargerWarning('Charger assignments could not be loaded. Counts may be incomplete.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const errors = attempted ? validateForm(formData) : {};

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingTariff(null);
    setAttempted(false);
  };

  const handleEdit = (tariff) => {
    setEditingTariff(tariff);
    setAttempted(false);
    setFormData({
      name: tariff.name || '',
      currency: 'AED',
      fixed_session_fee: tariff.fixed_session_fee ?? 0,
      price_per_kwh: tariff.price_per_kwh ?? 0,
      parking_fee_per_minute: tariff.parking_fee_per_minute ?? 0,
      parking_grace_period_minutes: tariff.parking_grace_period_minutes ?? 0,
      is_active: tariff.is_active !== false,
      time_of_use_rates: (tariff.time_of_use_rates || []).map((rate) => ({ ...rate })),
    });
    setDialogOpen(true);
  };

  const handleSave = async (event) => {
    event?.preventDefault();
    setAttempted(true);
    if (Object.keys(validateForm(formData)).length) return;
    setSaving(true);
    try {
      const payload = toPayload(formData);
      if (editingTariff) {
        await tariffsApi.update(editingTariff.id, payload);
        toast.success('Tariff updated');
      } else {
        await tariffsApi.create(payload);
        toast.success('Tariff created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData({ silent: true });
    } catch (error) {
      toast.error(apiMessage(error, 'Failed to save tariff'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await tariffsApi.delete(pendingDelete.id);
      toast.success('Tariff deleted');
      setPendingDelete(null);
      fetchData({ silent: true });
    } catch (error) {
      toast.error(apiMessage(error, 'Failed to delete tariff'));
    } finally {
      setDeleting(false);
    }
  };

  const addTimeOfUseRate = () => {
    setFormData((current) => ({
      ...current,
      time_of_use_rates: [...current.time_of_use_rates, { start_hour: 0, end_hour: 6, price_per_kwh: 0 }],
    }));
  };

  const updateTimeOfUseRate = (index, field, value) => {
    setFormData((current) => ({
      ...current,
      time_of_use_rates: current.time_of_use_rates.map((rate, rateIndex) => (
        rateIndex === index ? { ...rate, [field]: value } : rate
      )),
    }));
  };

  const removeTimeOfUseRate = (index) => {
    setFormData((current) => ({
      ...current,
      time_of_use_rates: current.time_of_use_rates.filter((_, rateIndex) => rateIndex !== index),
    }));
  };

  const assignedCount = (tariffId) => chargers.filter((charger) => charger.tariff_plan_id === tariffId).length;
  const touTariffs = tariffs.filter((tariff) => tariff.time_of_use_rates?.length > 0);

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  return (
    <PageShell testId="tariffs-page">
      <PageHeader
        eyebrow="Pricing"
        title="Tariffs"
        description="Set session, energy, and parking prices for your chargers."
        actions={(
          <Button type="button" className={pillButton} disabled={!canManage} onClick={openCreate} data-testid="add-tariff-btn">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add tariff
          </Button>
        )}
      />
      {!canManage ? (
        <p className={cn(glassSubtle, 'px-4 py-2 text-sm text-muted-foreground')}>
          You can view tariff plans. Creating or changing them requires the tariffs.manage permission.
        </p>
      ) : null}

      {loading ? (
        <div className="space-y-3" aria-busy="true" aria-live="polite">
          <span className="sr-only">Loading tariffs</span>
          {[0, 1, 2].map((item) => (
            <div key={item} className={cn(glassSubtle, 'h-16 animate-pulse')} />
          ))}
        </div>
      ) : null}

      {!loading && loadError ? <ErrorState message={loadError} onRetry={fetchData} /> : null}

      {!loading && !loadError ? (
        <>
          {chargerWarning ? (
            <p role="status" className={cn(glassSubtle, 'px-4 py-2 text-sm text-amber-800 dark:text-amber-200')}>{chargerWarning}</p>
          ) : null}
          <Card className={glassPanel}>
            <CardHeader>
              <CardTitle>Tariff plans</CardTitle>
              <CardDescription>Prices are billed in AED.</CardDescription>
            </CardHeader>
            <CardContent>
              {tariffs.length === 0 ? (
                <EmptyState
                  icon={DollarSign}
                  testId="no-tariffs"
                  title="No tariff plans yet"
                  description="Create a plan before assigning prices to chargers."
                  action={canManage ? (
                    <Button type="button" className={pillButton} onClick={openCreate}>Add tariff</Button>
                  ) : null}
                />
              ) : (
                <>
                  <ScrollHint />
                  <Table className="min-w-[760px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Base rate</TableHead>
                        <TableHead>Session fee</TableHead>
                        <TableHead>Parking fee</TableHead>
                        <TableHead>Assigned</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tariffs.map((tariff) => {
                        const assigned = assignedCount(tariff.id);
                        return (
                          <TableRow key={tariff.id} data-testid={`tariff-row-${tariff.id}`}>
                            <TableCell className="font-medium">{tariff.name}</TableCell>
                            <TableCell className="font-mono">{formatCurrency(tariff.price_per_kwh, tariff.currency)}/kWh</TableCell>
                            <TableCell className="font-mono">{formatCurrency(tariff.fixed_session_fee, tariff.currency)}</TableCell>
                            <TableCell className="font-mono">
                              {tariff.parking_fee_per_minute > 0
                                ? `${formatCurrency(tariff.parking_fee_per_minute, tariff.currency)}/min`
                                : '—'}
                            </TableCell>
                            <TableCell>{assigned} {assigned === 1 ? 'charger' : 'chargers'}</TableCell>
                            <TableCell>
                              <span className={cn(
                                'inline-flex rounded-full px-2.5 py-1 text-xs font-medium',
                                tariff.is_active
                                  ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200'
                                  : 'bg-muted text-muted-foreground'
                              )}
                              >
                                {tariff.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className={pillButton}
                                  disabled={!canManage}
                                  onClick={() => handleEdit(tariff)}
                                  aria-label={`Edit ${tariff.name}`}
                                  data-testid={`edit-tariff-${tariff.id}`}
                                >
                                  <Pencil className="h-4 w-4" aria-hidden="true" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className={pillButton}
                                  disabled={!canManage}
                                  onClick={() => setPendingDelete({ ...tariff, assigned })}
                                  aria-label={`Delete ${tariff.name}`}
                                  data-testid={`delete-tariff-${tariff.id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-red-700 dark:text-red-300" aria-hidden="true" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>

          {touTariffs.length > 0 ? (
            <Card className={glassPanel}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" aria-hidden="true" />
                  Time-of-use pricing
                </CardTitle>
                <CardDescription>These windows replace the base energy price while they are active.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2">
                {touTariffs.map((tariff) => (
                  <div key={tariff.id} className={cn(glassSubtle, 'p-4')}>
                    <h2 className="font-medium">{tariff.name}</h2>
                    <div className="mt-3 grid gap-2">
                      {tariff.time_of_use_rates.map((rate, index) => (
                        <div key={`${tariff.id}-${index}`} className="flex items-center justify-between gap-3 text-sm">
                          <span className="text-muted-foreground">{rate.start_hour}:00 – {rate.end_hour}:00</span>
                          <span className="font-mono">{formatCurrency(rate.price_per_kwh, tariff.currency)}/kWh</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ) : null}
        </>
      ) : null}

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (saving) return;
          setDialogOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className={cn(glassStrong, 'max-h-[90vh] overflow-y-auto sm:max-w-2xl')}>
          <DialogHeader>
            <DialogTitle>{editingTariff ? 'Edit tariff' : 'Create tariff'}</DialogTitle>
            <DialogDescription>Prices apply to new charging sessions that use this plan.</DialogDescription>
          </DialogHeader>
          <form className="space-y-6" onSubmit={handleSave} noValidate>
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="tariff-name"
                label="Name"
                required
                value={formData.name}
                error={errors.name}
                placeholder="Standard rate"
                data-testid="tariff-name"
                onChange={(event) => setFormData({ ...formData, name: event.target.value })}
              />
              <Field id="currency-fixed" label="Currency" hint="Tariffs are billed in UAE Dirham.">
                <div id="currency-fixed" className={cn(glassSubtle, 'flex h-10 items-center px-3 text-sm font-medium')} data-testid="currency-fixed">
                  AED
                </div>
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="fixed-fee"
                label="Fixed session fee"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                required
                value={formData.fixed_session_fee}
                error={errors.fixed_session_fee}
                data-testid="fixed-fee"
                onChange={(event) => setFormData({ ...formData, fixed_session_fee: event.target.value })}
              />
              <TextField
                id="price-per-kwh"
                label="Price per kWh"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                required
                value={formData.price_per_kwh}
                error={errors.price_per_kwh}
                data-testid="price-per-kwh"
                onChange={(event) => setFormData({ ...formData, price_per_kwh: event.target.value })}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="parking-fee"
                label="Parking fee per minute"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                required
                value={formData.parking_fee_per_minute}
                error={errors.parking_fee_per_minute}
                data-testid="parking-fee"
                onChange={(event) => setFormData({ ...formData, parking_fee_per_minute: event.target.value })}
              />
              <TextField
                id="grace-period"
                label="Grace period (minutes)"
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                required
                value={formData.parking_grace_period_minutes}
                error={errors.parking_grace_period_minutes}
                hint="Parking fees start after this many minutes."
                data-testid="grace-period"
                onChange={(event) => setFormData({ ...formData, parking_grace_period_minutes: event.target.value })}
              />
            </div>

            <div className="space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <Label id="tou-label">Time-of-use rates</Label>
                  <p className="text-xs text-muted-foreground">Optional windows with their own energy price.</p>
                </div>
                <Button type="button" variant="outline" size="sm" className={pillButton} onClick={addTimeOfUseRate} data-testid="add-tou-rate">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Add rate
                </Button>
              </div>
              {errors.time_of_use_rates ? (
                <p role="alert" className="text-sm text-destructive">{errors.time_of_use_rates}</p>
              ) : null}
              {formData.time_of_use_rates.map((rate, index) => (
                <div key={index} className={cn(glassSubtle, 'flex flex-col gap-3 p-3 sm:flex-row sm:items-end')}>
                  <div className="grid flex-1 gap-2 sm:grid-cols-3">
                    <div className="space-y-1">
                      <Label htmlFor={`tou-start-${index}`}>Start hour</Label>
                      <Input
                        id={`tou-start-${index}`}
                        type="number"
                        min="0"
                        max="23"
                        value={rate.start_hour}
                        className={fieldControl}
                        aria-label={`Rate ${index + 1} start hour`}
                        onChange={(event) => updateTimeOfUseRate(index, 'start_hour', event.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`tou-end-${index}`}>End hour</Label>
                      <Input
                        id={`tou-end-${index}`}
                        type="number"
                        min="0"
                        max="24"
                        value={rate.end_hour}
                        className={fieldControl}
                        aria-label={`Rate ${index + 1} end hour`}
                        onChange={(event) => updateTimeOfUseRate(index, 'end_hour', event.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`tou-price-${index}`}>Price per kWh</Label>
                      <Input
                        id={`tou-price-${index}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={rate.price_per_kwh}
                        className={fieldControl}
                        aria-label={`Rate ${index + 1} price per kWh`}
                        onChange={(event) => updateTimeOfUseRate(index, 'price_per_kwh', event.target.value)}
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={pillButton}
                    onClick={() => removeTimeOfUseRate(index)}
                    aria-label={`Remove rate ${index + 1}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-700 dark:text-red-300" aria-hidden="true" />
                  </Button>
                </div>
              ))}
            </div>

            <div className={cn(glassSubtle, 'flex items-center justify-between px-3 py-3')}>
              <Label htmlFor="active-switch">Active</Label>
              <Switch
                id="active-switch"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                aria-label="Tariff is active"
                data-testid="active-switch"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" className={pillButton} disabled={saving} onClick={() => { setDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className={pillButton} disabled={saving} data-testid="save-tariff-btn">
                {saving ? 'Saving…' : editingTariff ? 'Update tariff' : 'Create tariff'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => { if (!open) setPendingDelete(null); }}
        title={`Delete ${pendingDelete?.name || 'this tariff'}?`}
        description={pendingDelete?.assigned
          ? `This plan is assigned to ${pendingDelete.assigned} ${pendingDelete.assigned === 1 ? 'charger' : 'chargers'}. Deleting it does not update those chargers, so they may still reference a missing plan.`
          : 'This permanently removes the tariff plan.'}
        confirmLabel="Delete tariff"
        destructive
        pending={deleting}
        onConfirm={handleDelete}
      />
    </PageShell>
  );
};

export default TariffsPage;
